/**
 * Copyright (c) 2021 Signant Health. All Rights Reserved. Company Confidential.
 */

import React, { useState } from 'react';
import { connect } from 'react-redux';
import * as R from 'ramda';

import { Button } from 'tc-client-common';
import { hideModal, showModal } from '../../actions/actions';
import { Form, FormControl, FormGroup, Grid, ListGroupItem, Radio, Row } from 'react-bootstrap';
import { MODAL_TYPE_CHANGE_ANSWERS_OPTIN_OPTOUT } from '../../constants/modalTypes';
import {
  CHANGE_ANSWERS_ADDENDUM_STEP_DESCRIPTION,
  CHANGE_ANSWERS_ADDENDUM_RADIO_GENERATE,
  CHANGE_ANSWERS_OPTIN_OPTOUT,
  VERSION,
  CHANGE_ANSWERS_ADDENDUM_RADIO_UPLOAD,
  TOO_LARGE_ERROR,
  WRONG_TYPE_ERROR,
  CANCEL_BTN,
  FINISH_BTN,
  PREVIOUS_BTN
} from '../../const';
import { clearConsentDocuments4SoPChangeAnswers, saveChangeAnswers } from '../../actions/changeAnswers4sop.actions';
import Notification from '../common/notification/notification.component';
import DocumentPdfInput from './components/document-pdf-input';

const ChangeAnswersAddendumComponent = (props) => {
  const { addendumType: providedAddendumType, addendumFile: providedAddendumFile } = props.props;

  const [addendumType, setAddendumType] = useState(providedAddendumType || 'generate');
  const [pdfFile, setPdfFile] = useState(providedAddendumFile);
  const [pdfFileError, setPdfFileError] = useState();

  const submit = (e) => {
    e.preventDefault();

    const { participantId, participantJourneyGid, studyGid } = props;
    props.next({ ...props.props, participantId, participantJourneyGid, studyGid, addendumFile: pdfFile });
  };

  const handleAddendumTypeChange = (e) => {
    setAddendumType(e.target.value);
    if (e.target.value === 'generate') {
      setPdfFile(null);
      setPdfFileError(null);
    }
  };
  const onPdfInputChange = (e) => {
    e.preventDefault();

    const file = e.target.files[0];

    if (!(file.name.toLowerCase().endsWith('.pdf') && file.type === 'application/pdf')) {
      setPdfFileError(WRONG_TYPE_ERROR);
      return;
    }

    const sizeInMB = (file.size / (1024 * 1024)).toFixed(2);
    if (sizeInMB > 8) {
      setPdfFileError(TOO_LARGE_ERROR);
      return;
    }

    setPdfFileError(null);
    setPdfFile(file);
  };

  const thereIsAnError = props.error || pdfFileError;

  const errorBody = thereIsAnError && (
    <Row key={0}>
      <Notification type="error" data={thereIsAnError} />
    </Row>
  );

  const isFinishButtonDisabled = addendumType === 'upload' && !pdfFile;

  return (
    <div>
      <Form horizontal id="change-answers-addendum-form" onSubmit={(e) => submit(e)}>
        <Grid fluid>
          {errorBody}
          <Row key={1}>
            <FormGroup>
              <FormControl.Static>{CHANGE_ANSWERS_ADDENDUM_STEP_DESCRIPTION}</FormControl.Static>
              <br />
            </FormGroup>
          </Row>
          <Row key={2}>
            <ListGroupItem key={1}>
              <Radio
                key={1}
                value={'generate'}
                checked={addendumType === 'generate'}
                onChange={handleAddendumTypeChange}
              >
                {CHANGE_ANSWERS_ADDENDUM_RADIO_GENERATE}
              </Radio>
            </ListGroupItem>
            <ListGroupItem key={2}>
              <Radio key={2} value={'upload'} checked={addendumType === 'upload'} onChange={handleAddendumTypeChange}>
                {CHANGE_ANSWERS_ADDENDUM_RADIO_UPLOAD}
              </Radio>
              {addendumType === 'upload' && (
                <DocumentPdfInput
                  pdfFile={pdfFile}
                  setPdfFile={setPdfFile}
                  setError={setPdfFileError}
                  onPdfInputChange={onPdfInputChange}
                />
              )}
            </ListGroupItem>
          </Row>
          <br />
        </Grid>

        <div className="edit-modal-footer">
          <hr className="edit-modal-divider" />
          <Button
            theme="default large-button"
            label={PREVIOUS_BTN}
            handleClick={() => props.previous({ ...props.props, addendumType, addendumFile: pdfFile })}
          />
          <Button
            id="select-addendum-type-cancel-button"
            type="reset"
            theme="default large-button"
            handleClick={() => props.cancel()}
            label={CANCEL_BTN}
          />
          <Button theme="active large-button" label={FINISH_BTN} disabled={isFinishButtonDisabled} type="submit" />
        </div>
      </Form>
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  return {
    studyGid: R.path(['subject', 'participantJourney', 'studyGid'], state),
    participantId: R.path(['subject', 'participantJourney', 'userId'], state),
    participantJourneyGid: R.path(['subject', 'participantJourney', 'gid'], state),
    error: R.path(['changeAnswers', 'error'], state)
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    cancel: () => {
      dispatch(clearConsentDocuments4SoPChangeAnswers());
      dispatch(hideModal());
    },
    next: (props) => {
      const {
        consentingGid,
        studyGid,
        participantId,
        participantJourneyGid,
        signatureDate,
        reason,
        choiceAnswers,
        addendumFile
      } = props;

      const localTimezoneOffsetInMs = new Date().getTimezoneOffset() * 60 * 1000;
      const signatureDateWithLocalTimezoneOffsetAsISOString = new Date(
        signatureDate.getTime() - localTimezoneOffsetInMs
      ).toISOString();

      dispatch(
        saveChangeAnswers({
          consentingGid,
          studyGid,
          participantId,
          participantJourneyGid,
          signatureDate: signatureDateWithLocalTimezoneOffsetAsISOString,
          reason,
          choiceAnswers,
          addendumFile
        })
      );
    },
    previous: (props) => {
      const { consentName, consentVersion } = props;

      dispatch(
        showModal(MODAL_TYPE_CHANGE_ANSWERS_OPTIN_OPTOUT, {
          shouldCloseOnOverlayClick: false,
          title: CHANGE_ANSWERS_OPTIN_OPTOUT,
          details: [consentName, '-', VERSION, consentVersion].join(' '),
          className: 'document-status-optinoptout-modal',
          props: props
        })
      );
    }
  };
};

const ChangeAnswersAddendumContainer = connect(mapStateToProps, mapDispatchToProps)(ChangeAnswersAddendumComponent);

const ChangeAnswersAddendumModal = (props) => {
  return (
    <div>
      <ChangeAnswersAddendumContainer {...props} />
    </div>
  );
};

export { ChangeAnswersAddendumContainer, mapStateToProps, mapDispatchToProps, ChangeAnswersAddendumComponent };

export default ChangeAnswersAddendumModal;
